import type { Locale, Menu, MenuItem } from "@toema/shared-types";

/** The locale this landing reads the menu in. */
export const LOCALE: Locale = "en";

/** A menu item flattened to what a row actually renders. */
export interface DisplayItem {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Integer cents. */
  readonly priceCents: number;
}

export interface DisplayGroup {
  readonly id: string;
  readonly name: string;
  readonly items: readonly DisplayItem[];
}

/**
 * The item's name and description in `LOCALE`, falling back to whatever
 * translation exists.
 *
 * The fallback matters: an item added through the admin app in French only
 * should still appear on the menu in French rather than appear as a blank row
 * or vanish. The SKU is the last resort so a row always has a name.
 */
function translate(item: MenuItem): { name: string; description: string } {
  const preferred = item.translations.find((t) => t.locale === LOCALE);
  const chosen = preferred ?? item.translations[0];
  return {
    name: chosen?.name ?? item.sku,
    description: chosen?.description ?? "",
  };
}

/** Items a customer can actually order right now. */
function isOrderable(item: MenuItem): boolean {
  return item.isAvailable && item.deletedAt === null;
}

function toDisplayItem(item: MenuItem): DisplayItem {
  const { name, description } = translate(item);
  return { id: item.id, name, description, priceCents: item.priceCents };
}

const bySortOrder = (a: { sortOrder: number }, b: { sortOrder: number }): number =>
  a.sortOrder - b.sortOrder;

/**
 * Groups the menu into the sections the page prints, in `sortOrder`.
 *
 * Empty groups are dropped — an active category with everything sold out would
 * otherwise print a numbered heading over nothing. Items whose category is
 * missing or inactive are collected into a trailing group instead of being
 * discarded, because silently not selling something is a worse failure than an
 * oddly-named section.
 */
export function groupMenu(menu: Menu): readonly DisplayGroup[] {
  const orderable = menu.items.filter(isOrderable);
  const claimed = new Set<string>();

  const groups: DisplayGroup[] = [];

  for (const category of [...menu.categories].filter((c) => c.isActive).sort(bySortOrder)) {
    const items = orderable.filter((i) => i.categoryId === category.id);
    for (const i of items) claimed.add(i.id);
    if (items.length === 0) continue;
    groups.push({
      id: category.id,
      name: category.nameDefault,
      items: [...items].sort(bySortOrder).map(toDisplayItem),
    });
  }

  const orphans = orderable.filter((i) => !claimed.has(i.id));
  if (orphans.length > 0) {
    groups.push({
      id: "uncategorised",
      name: "Also on",
      items: [...orphans].sort(bySortOrder).map(toDisplayItem),
    });
  }

  return groups;
}

/** A group's number as the rule prints it: 01, 02, 03. */
export function groupNumber(index: number): string {
  return String(index + 1).padStart(2, "0");
}
